import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { canAct, canViewPath } from "../lib/permissions";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated user
  const [user, setUser] = useState(null);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      // ignore — we clear the local state regardless
    }
    setUser(false);
  }, []);

  const permissions = user && typeof user === "object" ? user.permissions || {} : {};

  const helpers = useMemo(
    () => ({
      can: (mod, action) => canAct(permissions, mod, action),
      canView: (pathname) => canViewPath(permissions, pathname),
    }),
    [permissions]
  );

  return (
    <AuthContext.Provider
      value={{ user, login, logout, refreshMe, permissions, ...helpers }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
